import { useState } from 'react';
import type { Broadcast, BroadcastChannel } from '../types';
import { RichTextEditor } from './RichTextEditor';

interface Props {
  draft: Partial<Broadcast>;
  onChange: (patch: Partial<Broadcast>) => void;
}

export const BroadcastComposer = ({ draft, onChange }: Props) => {
  const [imageMode, setImageMode] = useState<'url' | 'none'>('none');

  const toggleChannel = (ch: BroadcastChannel) => {
    const current = draft.channels ?? [];
    const next = current.includes(ch) ? current.filter((c) => c !== ch) : [...current, ch];
    if (next.length > 0) onChange({ channels: next });
  };

  const channels = draft.channels ?? [];

  return (
    <div className="space-y-5">
      {/* Channels */}
      <div>
        <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-2 block">
          Canais de envio
        </label>
        <div className="flex gap-3 flex-wrap">
          {([
            {
              id: 'internal' as BroadcastChannel,
              label: 'Notificação interna',
              icon: 'ri-notification-3-line',
              desc: 'Aparece no app do cliente',
            },
            {
              id: 'email' as BroadcastChannel,
              label: 'E-mail',
              icon: 'ri-mail-send-line',
              desc: 'Via e-mail transacional',
            },
          ]).map((ch) => {
            const active = channels.includes(ch.id);
            return (
              <button
                key={ch.id}
                type="button"
                onClick={() => toggleChannel(ch.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer ${
                  active
                    ? 'bg-violet-600/20 border-violet-500/40 text-white'
                    : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:text-white'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${ch.icon} text-[18px]`} />
                </div>
                <div className="text-left">
                  <p className="text-[13px] font-600">{ch.label}</p>
                  <p className="text-[11px] text-white/30">{ch.desc}</p>
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    active ? 'border-violet-400 bg-violet-500' : 'border-white/20'
                  }`}
                >
                  {active && <i className="ri-check-line text-white text-[10px]" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-1.5 block">
          Título <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={draft.title ?? ''}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Ex: Nova funcionalidade disponível! 🚀"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] placeholder-white/20 focus:outline-none focus:border-violet-500/60 transition-colors"
        />
      </div>

      {/* Subtitle */}
      <div>
        <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-1.5 block">
          Subtítulo{' '}
          <span className="text-white/20 font-400 normal-case tracking-normal">opcional</span>
        </label>
        <input
          type="text"
          value={draft.subtitle ?? ''}
          onChange={(e) => onChange({ subtitle: e.target.value || null })}
          placeholder="Ex: Confira o que preparamos para você"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] placeholder-white/20 focus:outline-none focus:border-violet-500/60 transition-colors"
        />
      </div>

      {/* Rich Content Editor */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider">
            Conteúdo <span className="text-red-400">*</span>
          </label>
          <span className="text-[11px] text-white/20">
            Suporte a negrito, itálico, listas, links, emojis e imagens
          </span>
        </div>
        <RichTextEditor
          value={draft.content ?? ''}
          onChange={(html) => onChange({ content: html })}
          placeholder="Escreva o conteúdo do comunicado... Use a toolbar acima para formatar."
          minHeight={240}
        />
      </div>

      {/* Banner Image */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider">
            Banner / Imagem de topo{' '}
            <span className="text-white/20 font-400 normal-case tracking-normal">opcional</span>
          </label>
          <div className="flex items-center gap-1">
            {(['none', 'url'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setImageMode(m);
                  if (m === 'none') onChange({ image_url: null });
                }}
                className={`px-3 py-1 rounded-lg text-[11px] font-600 transition-colors cursor-pointer ${
                  imageMode === m ? 'bg-violet-600/30 text-violet-300' : 'bg-white/5 text-white/30 hover:bg-white/10'
                }`}
              >
                {m === 'none' ? 'Sem banner' : 'URL do banner'}
              </button>
            ))}
          </div>
        </div>
        {imageMode === 'url' && (
          <div className="space-y-2">
            <input
              type="url"
              value={draft.image_url ?? ''}
              onChange={(e) => onChange({ image_url: e.target.value || null })}
              placeholder="https://exemplo.com/banner.jpg"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] placeholder-white/20 focus:outline-none focus:border-violet-500/60 transition-colors"
            />
            {draft.image_url && (
              <div className="relative rounded-xl overflow-hidden border border-white/10 h-40">
                <img
                  src={draft.image_url}
                  alt="Banner preview"
                  className="w-full h-full object-cover object-top"
                />
                <button
                  type="button"
                  onClick={() => onChange({ image_url: null })}
                  className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/60 rounded-full text-white hover:bg-black/80 cursor-pointer"
                >
                  <i className="ri-close-line text-[14px]" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CTA */}
      <div>
        <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-1.5 block">
          Botão de ação (CTA){' '}
          <span className="text-white/20 font-400 normal-case tracking-normal">opcional</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={draft.cta_label ?? ''}
            onChange={(e) => onChange({ cta_label: e.target.value || null })}
            placeholder="Ex: Ver mais"
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] placeholder-white/20 focus:outline-none focus:border-violet-500/60 transition-colors"
          />
          <input
            type="url"
            value={draft.cta_url ?? ''}
            onChange={(e) => onChange({ cta_url: e.target.value || null })}
            placeholder="https://..."
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] placeholder-white/20 focus:outline-none focus:border-violet-500/60 transition-colors"
          />
        </div>
      </div>
    </div>
  );
};
